import { ClientCampaign, getClientCampaign } from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../../../hooks/useAbortableEffect';

export type UseCampaignDetailsResult = {
    isInitialized: boolean;
    isLoading: boolean;
    hasErrorLoading: boolean;
    campaign: ClientCampaign | null;
};

export function useCampaignDetails(campaignId: number | null): UseCampaignDetailsResult {
    const [isLoading, setIsLoading] = useState(false);
    const [campaign, setCampaign] = useState<ClientCampaign | null>(null);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const fetchCampaign = useCallback(async (id: number, requestInit?: RequestInit) => {
        try {
            setHasErrorLoading(false);
            setIsLoading(true);
            const response = await getClientCampaign(id, requestInit);
            if (response.status === 404) {
                setHasErrorLoading(true);
                return;
            }

            setCampaign(response.data);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            setHasErrorLoading(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (typeof campaignId === 'number') {
                fetchCampaign(campaignId, { signal });
            }
        },
        [campaignId, fetchCampaign]
    );

    return {
        isLoading,
        hasErrorLoading,
        campaign,
        isInitialized: !hasErrorLoading && !isLoading && !!campaign,
    };
}
